import * as React from 'react';
import styles from './index.less';
import { Space } from 'antd';
import FDrawer from '@/components/FDrawer';
import FComponentsLib from '@freelog/components-lib';
import FTooltip from '@/components/FTooltip';
import { FI18n, FServiceAPI, FUtil } from '@freelog/tools-lib';
import PolicyTemplates, { PolicyTemplate, PolicyTemplateInfo } from './PolicyTemplates';
import Editor, { DisplayItem } from './Editor';
import FInput_PinyinSafeTextCounter from '@/components/FInput_PinyinSafeTextCounter';
import FIcons from '@/components/FIcons';
import { Base64 } from 'js-base64';
import fMessage from '@/components/fMessage';
import fPromiseModalConfirm from '@/components/fPromiseModalConfirm';

interface FPolicyBuilderDrawer3Props {
  open?: boolean;

  alreadyUsedTitles?: string[];
  alreadyUsedTexts?: string[];

  nodeID?: number;
  resourceTypeCode: string[];
  defaultValue?: PolicyTemplateInfo;

  onConfirm?({ title, text }: { title: string; text: string }): void;

  onCancel?(): void;

  afterVisibleChange?(visible: boolean): void;
}

function FPolicyBuilderDrawer3({
  open = false,
  alreadyUsedTitles = [],
  alreadyUsedTexts = [],
  nodeID,
  resourceTypeCode,
  defaultValue,
  onConfirm,
  onCancel,
  afterVisibleChange,
}: FPolicyBuilderDrawer3Props) {
  const [$show, set$show, get$show] = FUtil.Hook.useGetState<'template' | 'editor' | 'confirm'>(
    'template',
  );

  const [$poclicyID, set$poclicyID, get$poclicyID] = FUtil.Hook.useGetState<string>('');
  const [$title, set$title, get$title] = FUtil.Hook.useGetState<string>('');
  const [$titleError, set$titleError, get$titleError] = FUtil.Hook.useGetState<string>('');
  const [$editorData, set$editorData, get$editorData] = FUtil.Hook.useGetState<DisplayItem[]>([]);

  FUtil.Hook.useGetState<string>('');
  const [$confirmPolicyCode, set$confirmPolicyCode, get$confirmPolicyCode] =
    FUtil.Hook.useGetState<string>('');
  const [$confirmPolicyTranslation, set$confirmPolicyTranslation, get$confirmPolicyTranslation] =
    FUtil.Hook.useGetState<string>('');

  function onSelectTemplate(templateInfo: PolicyTemplateInfo) {
    set$poclicyID(templateInfo.id);
    set$title(templateInfo.title);
    set$titleError(alreadyUsedTitles.includes(templateInfo.title) ? '策略名称已存在' : '');
    set$editorData(templateInfo.displayData);
    set$show('editor');
  }

  return (
    <FDrawer
      open={open}
      title={
        <Space size={10}>
          <FComponentsLib.FTitleText
            type='h2'
            text={
              // '添加授权策略'
              FI18n.i18nNext.tAuto('createauthplan_title')
            }
          />
          <FTooltip title={FI18n.i18nNext.tJSXElement('addauth_info')}>
            <label
              // onClick={() => {
              //   self.open('https://www.yuque.com/taiyang-4rbf5/vctf9v/kl3f01');
              // }}
              style={{
                cursor: 'pointer',
                width: 20,
                height: 20,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#E9E9E9',
                fontSize: 12,
              }}
            >
              ?
            </label>
          </FTooltip>
        </Space>
      }
      width={720}
      closable={false}
      topRight={
        <>
          {$show === 'template' && (
            <FComponentsLib.FRectBtn type='default' onClick={onCancel}>
              {
                // 取消
                FI18n.i18nNext.tAuto('btn_cancel')
              }
            </FComponentsLib.FRectBtn>
          )}

          {$show === 'editor' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <FComponentsLib.FRectBtn type='default' onClick={onCancel}>
                {/* 取消 */}
                {FI18n.i18nNext.tAuto('btn_cancel')}
              </FComponentsLib.FRectBtn>
              <FComponentsLib.FRectBtn
                type='default'
                onClick={() => {
                  set$show('template');
                  set$poclicyID('');
                  set$editorData([]);
                }}
              >
                {
                  // 上一步
                  FI18n.i18nNext.tAuto('createauthplan_btn_back')
                }
              </FComponentsLib.FRectBtn>
              <FComponentsLib.FRectBtn
                type='primary'
                disabled={
                  $title === '' ||
                  $titleError !== '' ||
                  $editorData.some((item) => {
                    return (
                      (item.type === 'number' && !item.number?.value) ||
                      (item.type === 'select' && !item.select?.value) ||
                      (item.type === 'datetime' && !item.datetime?.value)
                    );
                  })
                }
                onClick={async () => {
                  const { data } = await FServiceAPI.Policy.policyReCompile({
                    _id: $poclicyID,
                    fillArgs: $editorData
                      .filter((item) => {
                        return (
                          item.type === 'number' ||
                          item.type === 'select' ||
                          item.type === 'datetime'
                        );
                      })
                      .map((item) => {
                        return {
                          name: item.id,
                          value:
                            item.number?.value || item.select?.value || item.datetime?.value || '',
                        };
                      }),
                  });

                  const policyCode = data.contractNew;

                  if (alreadyUsedTexts.includes(policyCode)) {
                    fMessage('策略代码已存在', 'error');
                    return;
                  }

                  const policyCodeEncoded: string = (policyCode || '').replace(/(\t|\r)/g, ' ');
                  const policyCodeEncodedEncoded: string = Base64.encode(policyCodeEncoded);
                  const { data: policyTranslation }: { data: string } =
                    await FServiceAPI.Policy.policyTranslation({
                      contract: policyCodeEncodedEncoded,
                    });
                  set$confirmPolicyCode(policyCode);
                  set$confirmPolicyTranslation(policyTranslation);
                  set$show('confirm');
                }}
              >
                {
                  // 下一步
                  FI18n.i18nNext.tAuto('createauthplan_btn_continue')
                }
              </FComponentsLib.FRectBtn>
            </div>
          )}

          {$show === 'confirm' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <FComponentsLib.FRectBtn type='default' onClick={onCancel}>
                {
                  // 取消
                  FI18n.i18nNext.tAuto('btn_cancel')
                }
              </FComponentsLib.FRectBtn>
              <FComponentsLib.FRectBtn
                type='default'
                onClick={() => {
                  set$show('editor');
                }}
              >
                {
                  // 上一步
                  FI18n.i18nNext.tAuto('createauthplan_btn_back')
                }
              </FComponentsLib.FRectBtn>
              <FComponentsLib.FRectBtn
                type='primary'
                onClick={() => {
                  onConfirm?.({ title: get$title(), text: get$confirmPolicyCode() });
                }}
              >
                {
                  // 创建
                  FI18n.i18nNext.tAuto('createauthplan_btn_submit')
                }
              </FComponentsLib.FRectBtn>
            </div>
          )}
        </>
      }
      afterOpenChange={(open) => {
        afterVisibleChange?.(open);
        if (!open) {
          set$show('template');
          set$poclicyID('');
          set$editorData([]);
        } else {
          if (defaultValue) {
            onSelectTemplate(defaultValue);
          }
        }
      }}
    >
      {$show === 'template' && (<div>
        <div className={styles.SelectTemplateTip}>
          <FIcons.FInfo style={{ fontSize: 12 }} />
          <div style={{ width: 5 }} />
          <span>{
            // '选择模版后可对其进行编辑'
            FI18n.i18nNext.tAuto('createauthplan_info_choosetemplate')
          }</span>
        </div>
        <div style={{ height: 30 }} />
        <PolicyTemplates
          resourceTypeCode={resourceTypeCode}
          nodeID={nodeID}
          onSelect={async (templateInfo) => {
            if (
              // templateInfo.displayData.some((item) => {
              //   return item.id.includes('.TransactionEvent');
              // })
              templateInfo.code.includes('.TransactionEvent')
            ) {
              if (await canAddTransactionEventPolicy({ nodeID })) {
                onSelectTemplate(templateInfo);
              }
              return;
            }
            onSelectTemplate(templateInfo);
          }}
        />
      </div>)}
      {$show === 'editor' && (
        <div>
          <FComponentsLib.FContentText
            type='additional2'
            text={
              // '策略标题'
              FI18n.i18nNext.tAuto('createauthplan_input_title')
            }
            style={{ color: '#222' }}
          />
          <div style={{ height: 10 }} />
          <FInput_PinyinSafeTextCounter
            lengthLimit={20}
            style={{ width: '100%' }}
            placeholder={'输入策略名称…'}
            value={$title}
            onChangeValue={(value) => {
              set$title(value);
              let errorText: string = '';
              if (alreadyUsedTitles.includes(value)) {
                errorText = '策略名称已存在';
              } else if (value === '') {
                errorText = '请输入策略名称';
              } else if (value.length < 2) {
                errorText = '不少于2个字符';
              }
              set$titleError(errorText);
            }}
            hasError={$titleError !== ''}
          />
          {$titleError !== '' && (
            <>
              <div style={{ height: 5 }} />
              <div style={{ color: '#EE4040' }}>{$titleError}</div>
            </>
          )}
          <div style={{ height: 30 }} />
          <FComponentsLib.FContentText
            type='additional2'
            text={
              // '策略方案'
              FI18n.i18nNext.tAuto('createauthplan_input_terms')
            }
            style={{ color: '#222' }}
          />
          <div style={{ height: 10 }} />
          {/* {console.log($editorData, '$editorData wi8ejfklsdlkj')} */}
          <Editor
            displayItems={$editorData}
            onChange_DisplayItems={(value) => {
              set$editorData(value);
            }}
          />
        </div>
      )}
      {$show === 'confirm' && (
        <div>
          <div className={styles.SelectTemplateTip}>
            <FIcons.FInfo style={{ fontSize: 12 }} />
            <div style={{ width: 5 }} />
            <span>{
              // '预览策略，返回编辑或确认创建'
              FI18n.i18nNext.tAuto('createauthplan_info_preview')
            }</span>
          </div>
          <div style={{ height: 30 }} />
          <PolicyTemplate
            title={$title}
            translation={$confirmPolicyTranslation}
            text={$confirmPolicyCode}
            showSelectBtn={false}
          />
        </div>
      )}
    </FDrawer>
  );
}

export default FPolicyBuilderDrawer3;

// 是否可以添加交易事件的策略
interface CanAddTransactionEventPolicy {
  nodeID?: number;
}

export async function canAddTransactionEventPolicy({
  nodeID,
}: CanAddTransactionEventPolicy): Promise<boolean> {
  const {
    ret,
    errCode,
    msg,
    data,
  }: {
    ret: number;
    errCode: number;
    msg: string;
    data: {
      status: number;
      latestOperationDate?: string;
    };
  } = await FServiceAPI.Payment.queryWithdrawStatus({
    ownerId: nodeID ? nodeID : FUtil.Tool.getUserIDByCookies(),
    accountType: nodeID ? 2 : 1,
  });
  if (data.status === 1) {
    if (
      await fPromiseModalConfirm({
        title: '提示',
        description: FI18n.i18nNext.t('createauthplan_alert_addpayoutmethod_msg'),
        okText: FI18n.i18nNext.t('createauthplan_alert_addpayoutmethod_btn_ok'),
        cancelText: FI18n.i18nNext.t('createauthplan_alert_addpayoutmethod_btn_later'),
        // promptKey_localStorage: 'policyTemplateTransactionEvent',
      })
    ) {
      self.open(FUtil.LinkTo.settlementInfoCreator({ nodeID }));
    }
    return false;
  }
  return true;
}
